import { ReactNode, useEffect, useState } from 'react';

import { Icon } from '@iconify/react';
import ContentApi from 'api/contentApi';
import { ICON_SIZES } from 'components/atoms/button/IconButton';
import { User } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import ContentService from 'services/contentService';
import { ContentData } from 'types/contentTypes';
import { BaseStatData } from 'types/statData';

import ButtonLink from '../../components/atoms/anchor/ButtonLink';
import Button from '../../components/atoms/button';
import EnrollmentService from '../../services/enrollmentService';
import SectionService from '../../services/sectionService';
import SectionData from '../../types/SectionData';
import SkillData from '../../types/skillData';

import './Banner.scss';
import { StatService } from '../../services/statService';

interface BannerProps {
  imageUrl?: string | null;
  user: User;
  skill: SkillData;
  className?: string;
}

export default function Banner({ imageUrl, skill, user, className = '' }: BannerProps) {
  const [isEnrolled, setIsEnrolled] = useState<boolean>();
  const [firstIncompleteSection, setFirstIncompleteSection] = useState<SectionData | null>();
  const [contentsInFirstIncompleteSection, setContentsInFirstIncompleteSection] = useState<ContentData[]>();
  const [incompleteContentsInFirstIncompleteSection, setIncompleteContentsInFirstIncompleteSection] =
    useState<ContentData[]>();
  const [completedSkillData, setCompletedSkillData] = useState<BaseStatData | undefined>();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    async function fetchData() {
      const fetchedIsEnrolled = await EnrollmentService.isUserEnrolledInSkill(user, skill);
      const fetchedFirstIncompleteSection = await SectionService.getFirstIncompleteSection(skill, user);
      const completedSkillData = await StatService.getTargetStatForUser(skill.id, user?.uid);

      if (fetchedFirstIncompleteSection) {
        const fetchedIncompleteContents = await ContentService.getIncompleteContentsForUser(
          fetchedFirstIncompleteSection,
          user
        );
        const contents = await ContentApi.getContentsBySectionId(fetchedFirstIncompleteSection.id);

        setIncompleteContentsInFirstIncompleteSection(fetchedIncompleteContents);
        setContentsInFirstIncompleteSection(contents);
      }
      setIsEnrolled(fetchedIsEnrolled);
      setFirstIncompleteSection(fetchedFirstIncompleteSection);
      setCompletedSkillData(completedSkillData);
    }

    fetchData();
  }, [user, skill]);

  async function handleStartClick(to: string) {
    await EnrollmentService.enrollUserInSkill(user, skill);
    navigate(to);
  }

  const bannerStyle = {
    backgroundImage: `url(${imageUrl}) `,
  };

  return (
    <div className={`banner ${className}`} style={bannerStyle}>
      {firstIncompleteSection && isEnrolled ? (
        // TODO add logic to restart button
        <ButtonLink
          className="start-continue-button"
          to={firstIncompleteSection ? `/skills/${skill.slug}/sections/${firstIncompleteSection.slug}` : '#'}
          disabled={firstIncompleteSection === null}
        >
          <ButtonContent>
            <p className="main-title">{firstIncompleteSection ? 'Continue' : 'Restart'}</p>
            {firstIncompleteSection ? (
              <p className="font-light">{firstIncompleteSection.name}</p>
            ) : (
              <p className="font-light">Compl. {completedSkillData?.created.toLocaleDateString()}</p>
            )}
          </ButtonContent>
        </ButtonLink>
      ) : (
        <Button
          className="start-continue-button"
          onClick={
            user
              ? firstIncompleteSection
                ? () => handleStartClick(`/skills/${skill.slug}/sections/${firstIncompleteSection.slug}`)
                : undefined
              : () =>
                  navigate({
                    pathname: '/register',
                    search: `?redirectTo=${pathname}`,
                  })
          }
          disabled={!firstIncompleteSection && !!user}
        >
          <ButtonContent>
            <p className="main-title">
              {user ? (firstIncompleteSection ? 'Get started' : 'Unavailable') : 'Sign in to start'}
            </p>
            {firstIncompleteSection && (
              <p className="font-light">
                {firstIncompleteSection.name}{' '}
                {contentsInFirstIncompleteSection!.length - incompleteContentsInFirstIncompleteSection!.length}/
                {contentsInFirstIncompleteSection?.length}
              </p>
            )}
          </ButtonContent>
        </Button>
      )}
    </div>
  );
}

interface ButtonContentProps {
  children?: ReactNode;
}

function ButtonContent({ children }: ButtonContentProps) {
  return (
    <div className="button-content-container">
      <div className="main-content">{children}</div>
      <div>
        <Icon
          className={'icon'}
          icon={`material-symbols:arrow-circle-right-outline`}
          height={ICON_SIZES.md}
          width={ICON_SIZES.md}
        />
      </div>
    </div>
  );
}
